import { Link, useSearchParams } from "react-router-dom";
import { BrandCorpus } from "../components/brand/BrandCorpus";
import { IndexedSourcesPanel } from "../components/brand/IndexedSourcesPanel";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useAddToCorpus, useBrand } from "../hooks/useBrand";

export function Corpus() {
  const { activeBrand, brands, isLoading } = useActiveBrand();
  const [searchParams] = useSearchParams();
  const requestedBrandId = searchParams.get("brandId");
  const requestedShopifyProductId = searchParams.get("shopifyProductId");
  const matchedBrand = requestedBrandId
    ? brands.find((brand) => brand.id === requestedBrandId)
    : undefined;
  const fallbackBrand = !requestedBrandId ? activeBrand : undefined;
  const routeBrandId = matchedBrand?.id ?? requestedBrandId ?? undefined;
  const routeBrandQuery = useBrand(requestedBrandId ? routeBrandId : undefined);
  const resolvedBrand = matchedBrand ?? routeBrandQuery.data?.data ?? fallbackBrand;
  const addCorpus = useAddToCorpus(resolvedBrand?.id ?? "");

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (requestedBrandId && routeBrandQuery.isLoading && !matchedBrand) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (requestedBrandId && !resolvedBrand) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 py-8">
        <WorkspaceHeader
          eyebrow="Brand Workspace"
          title="Brand not found"
          description="The requested brand could not be found for this corpus view."
          breadcrumbs={[
            { label: "Corpus" },
          ]}
        />
        <Link
          to="/brand"
          className="inline-flex rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900"
        >
          Go to brand setup
        </Link>
      </div>
    );
  }

  if (!resolvedBrand) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 py-8">
        <WorkspaceHeader
          eyebrow="Brand Workspace"
          title="No active brand selected"
          description="Select or create a brand before adding reusable context to the corpus."
          breadcrumbs={[
            { label: "Corpus" },
          ]}
        />
        <Link
          to="/brand"
          className="inline-flex rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400 hover:text-slate-900"
        >
          Go to brand setup
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5 py-4">
      <WorkspaceHeader
        eyebrow="Brand Workspace"
        title={resolvedBrand.name}
        description="Store long-lived brand context, findings, and reusable artifacts outside the settings shell."
        breadcrumbs={[
          { label: "Corpus" },
        ]}
        metadata={(
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
            Shared brand memory
          </span>
        )}
      />

      <div className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
        <BrandCorpus
          onAdd={(content) => addCorpus.mutate({ content })}
          adding={addCorpus.isPending}
        />
      </div>

      <IndexedSourcesPanel
        brandId={resolvedBrand.id}
        shopifyProductId={requestedShopifyProductId}
      />
    </div>
  );
}
